import { BaseComponent } from './baseComponent';

export class Modal extends BaseComponent {
    private body: HTMLBodyElement = document.querySelector('body');
    private trigger: HTMLElement;
    private modal: HTMLElement;
    private nonModalNodes: NodeListOf<HTMLElement>;
    private groupModals: NodeListOf<HTMLElement>;
    private activeIndex: number;
    private nextButton: HTMLElement;
    private prevButton: HTMLElement;

    constructor(container: HTMLElement, options: Record<string, any>) {
        super(container, options);

        this.trigger = this.container.querySelector('.modal-trigger');
        this.modal = this.container.querySelector('.modal');

        // used for tabindex
        this.nonModalNodes = document.querySelectorAll(`body *:not(#${this.container.id} *):not([tabindex="-1"])`);

        this.nextButton = this.container.querySelector('.modal-button-next');
        this.prevButton = this.container.querySelector('.modal-button-prev');
        if (this.options.group) {
            this.groupModals = document.querySelectorAll(`.modal[data-group="${this.options.group}"]`);
            this.activeIndex = Array.from(this.groupModals).findIndex(modal => modal.isEqualNode(this.modal));
        }

        if (!this.options.group || this.groupModals.length < 2) {
            this.nextButton.classList.add('is-hidden');
            this.prevButton.classList.add('is-hidden');
        }

        this.trigger.addEventListener('click', (event: Event): void => {
            event.preventDefault();
            this.open();
        });

        this.modal.addEventListener('open', (event: Event): void => {
            this.open();
        });

        this.onLoad();
    }

    next(): void {
        if (!this.options.group) return;
        this.close();
        const next = this.groupModals[this.activeIndex + 1] ?? this.groupModals[0];
        next.dispatchEvent(new Event('open'))
    }

    prev(): void {
        if (!this.options.group) return;
        this.close();
        const prev = this.groupModals[this.activeIndex - 1] ?? this.groupModals[this.groupModals.length - 1];
        prev.dispatchEvent(new Event('open'))
    }

    attachNavigationEvents() {
        this.container.addEventListener('keydown', (e: KeyboardEvent): void => {
            switch (e.key) {
                case 'Escape':
                    this.close();
                    break;

                case 'ArrowLeft':
                    this.prev();
                    break;

                case 'ArrowRight':
                    this.next();
                    break;
            }
        });

        this.nextButton.addEventListener('click', (e: Event): void => {
            this.next();
        });

        this.prevButton.addEventListener('click', (e: Event): void => {
            this.prev();
        });

        let touchstartX = 0
        let touchendX = 0
        this.modal.addEventListener('touchstart', (e: TouchEvent): void => {
            touchstartX = e.changedTouches[0].screenX;
        });
        this.modal.addEventListener('touchend', (e: TouchEvent): void => {
            touchendX = e.changedTouches[0].screenX;
            if (touchendX + 10 < touchstartX) {
                this.next();
                return;
            }

            if (touchendX > touchstartX + 10) {
                this.prev();
            }
        });

        // close modal when clicking outside of it
        const closeElements = this.container.querySelectorAll('.modal-background, .modal-close');
        closeElements.forEach(close => {
            close.addEventListener('click', () => {
                this.close();
            });
        });
    }

    open(): void {
        this.modal.classList.add('is-active');
        this.modal.setAttribute('aria-hidden', 'false');
        this.trigger.setAttribute('aria-expanded', 'true');
        this.body.classList.add('modal-open');

        // set tabindex to -1 of all nodes outside the opened modal to prevent focus outside of modal
        this.nonModalNodes.forEach((node: HTMLElement & {prevTabindex: number}): void => {
            const index = node.getAttribute('tabindex');
            if (index) node.prevTabindex = parseInt(index);
            node.setAttribute('tabindex', '-1');
        });
        (this.modal.querySelector('.modal-close') as HTMLElement).focus();

        this.afterOpen(this.modal.closest('.modal-container').id);
    }

    close(): void {
        this.modal.classList.remove('is-active');
        this.modal.setAttribute('aria-hidden', 'true');
        this.trigger.setAttribute('aria-expanded', 'false');
        this.body.classList.remove('modal-open');

        // reset tabindex
        this.nonModalNodes.forEach((node: HTMLElement & {prevTabindex: number}): void => {
            if (node.hasOwnProperty('prevTabindex') && node.prevTabindex !== null) {
                node.setAttribute('tabindex', node.prevTabindex.toString());
                node.prevTabindex = null;
            } else {
                node.removeAttribute('tabindex');
            }
        });

        this.trigger.focus();
        this.afterClose(this.modal.closest('.modal-container').id);
    }

    public onLoad(): void {
        this.attachNavigationEvents();

        if (!window.location.hash || !this.options.linkable) return;
        if (this.container.id === window.location.hash.replace('#', '')) this.open()
    }

    getDefaultOptions(options: Record<string, any> = {}) {
        const defaultOptions = {
            group: false,
        };
        return {
            ...super.getDefaultOptions(),
            ...defaultOptions,
            ...options,
        };
    }
}
